// src/generators/dzial3_funkcje/temat3_kwadratowa/podtemat1_ogolna_types/DeltaAndZerosCountTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import SelectInput from "../../../../components/inputs/SelectInput";

/* =============================================================
   TASK: DeltaAndZerosCountTask
   Cel: Dla f(x)=ax^2+bx+c oblicz Δ oraz wybierz liczbę miejsc zerowych.
   Wejście: FractionInput dla Δ + SelectInput (0/1/2) — krótki select.
   Fixy:
   - SelectInput kontrolowany (koniec z „brak odpowiedzi” po wyborze)
   - Jednolity zapis i odporne sprawdzanie (number / string / {value,label})
   - Label dla Δ w podglądzie FractionInput
   - Odporne renderUserAnswer na kolejność argumentów
   - Losowanie zgodne z preferencją 1:3:1 dla znaku Δ
============================================================= */

/* ---------- helpers ---------- */

function randInt(min, max, exclude = new Set()) {
  const span = max - min + 1;
  let v;
  do {
    v = Math.floor(Math.random() * span) + min;
  } while (exclude.has(v));
  return v;
}

// Ładny zapis ax^2 + bx + c
function term(coeff, latexBase, opts = { omitOne: false }) {
  if (coeff === 0) return "";
  const sign = coeff > 0 ? " + " : " - ";
  const abs = Math.abs(coeff);
  const num = abs === 1 && opts.omitOne ? "" : String(abs);
  return `${sign}${num}${latexBase}`;
}

function formatQuadraticLatex(a, b, c) {
  const aCoeff = Math.abs(a) === 1 ? (a === -1 ? "-" : "") : String(a);
  let out = `${aCoeff}x^2`;
  out += term(b, "x", { omitOne: true });
  if (c !== 0) out += (c > 0 ? " + " : " - ") + String(Math.abs(c));
  return out;
}

// Parser: "3/4", "-2 1/3", "1,25", "-4.5", "7"
function parseFractionLike(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;

  s = s.replace(/\u2212/g, "-").replace(",", ".");

  // liczba mieszana A B/C
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = parseInt(m[1], 10), B = parseInt(m[2], 10), C = parseInt(m[3], 10);
    if (!C) return null;
    const frac = B / C;
    return A >= 0 ? (A + frac) : (A - frac);
  }

  // zwykły ułamek P/Q
  m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const P = parseInt(m[1], 10), Q = parseInt(m[2], 10);
    if (!Q) return null;
    return P / Q;
  }

  // całkowita / dziesiętna
  if (/^[+-]?\d+(\.\d+)?$/.test(s)) return Number(s);
  return null;
}

function prettyRawToLatex(raw) {
  if (raw == null || String(raw).trim() === "") return "?";
  let s = String(raw).trim().replace(/\u2212/g, "-").replace(",", ".");
  // liczba mieszana
  let m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) return `${m[1]}\\;\\dfrac{${m[2]}}{${m[3]}}`;
  // zwykły ułamek
  m = s.match(/^\s*([+-]?\d+)\\?\/\\?(\d+)\s*$/);
  if (m) return `\\dfrac{${m[1]}}{${m[2]}}`;
  // dziesiętna -> przecinek
  if (/^[+-]?\d+\.\d+$/.test(s)) return s.replace(".", "{,}");
  return s;
}

// Normalizacja wartości z SelectInput: 0/1/2 lub null
function normalizeZeros(val) {
  if (val == null) return null;
  if (typeof val === "number") return val;
  if (typeof val === "string" && val.trim() !== "") return Number(val);
  if (typeof val === "object" && "value" in val) return Number(val.value);
  return null;
}

/* ---------- losowanie z wagami 1:3:1 dla znaku delty ---------- */

function pickDeltaSignWeighted() {
  // [-] : [0] : [+] = 1 : 3 : 1
  const bucket = Math.random() * 5; // [0,5)
  if (bucket < 1) return -1;
  if (bucket < 4) return 0;
  return 1;
}

function sampleABCWithDeltaSign(sign) {
  if (sign === 0) {
    // Δ = 0  => b^2 = 4ac
    const a = randInt(-5, 5, new Set([0]));
    const k = randInt(1, 6);
    // 4ac = (2k)^2  => c = k^2 / a (dobierzmy dzielnik)
    const divisors = [-1, 1, -k, k, -k * k, k * k, -2, 2, -4, 4].filter(d => d !== 0);
    const a2 = divisors.includes(a) ? a : divisors[randInt(0, divisors.length - 1)];
    const c = (k * k) / a2;
    const b = 2 * k;
    return { a: a2, b, c, delta: 0 };
  } else if (sign > 0) {
    // Δ > 0
    const a = randInt(-5, 5, new Set([0]));
    const b = randInt(-9, 9);
    let c;
    do {
      c = randInt(-9, 9);
    } while (b * b - 4 * a * c <= 0);
    return { a, b, c, delta: b * b - 4 * a * c };
  } else {
    // Δ < 0
    const a = randInt(-5, 5, new Set([0]));
    const b = randInt(-9, 9);
    let c;
    do {
      c = randInt(-9, 9);
    } while (b * b - 4 * a * c >= 0);
    return { a, b, c, delta: b * b - 4 * a * c };
  }
}

/* ---------- API ---------- */

export function generateTask() {
  const sign = pickDeltaSignWeighted();
  const { a, b, c, delta } = sampleABCWithDeltaSign(sign);
  return { type: "DeltaAndZerosCountTask", data: { a, b, c, delta } };
}

export function renderTask({ data }) {
  const { a, b, c } = data;
  const body = formatQuadraticLatex(a, b, c);

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <div>
        Dana jest funkcja w postaci ogólnej:{" "}
        <InlineMath math={'f(x) = ' + body} />
      </div>
      <div>
        Oblicz <InlineMath math={'\\Delta'} /> oraz wybierz liczbę miejsc zerowych funkcji.
      </div>
    </div>
  );
}

/* ---------- INPUT (FractionInput + SelectInput krótszy) ---------- */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const curr = (userAnswer && typeof userAnswer === "object") ? userAnswer : {};
  const deltaVal = curr.delta && typeof curr.delta === "object" && "raw" in curr.delta
    ? curr.delta
    : { raw: String(curr.delta ?? "") };

  // Wartość kontrolowana dla SelectInput:
  // - value przyjmuje string (jak w SignOfLineTask) albo obiekt {value,label}
  //   -> trzymamy wewnątrz STRING "0"/"1"/"2" (spójnie i najprościej)
  const zerosStr = (() => {
    const n = normalizeZeros(curr.zeros);
    return n == null ? "" : String(n);
  })();

  return (
    <div style={{ display: "grid", gap: 14, marginTop: 6 }}>
      <div style={{ display: "grid", gap: 6 }}>
        <InlineMath math={'\\Delta ='} />
        <FractionInput
          value={deltaVal}
          onChange={(v) => onChange({ ...curr, delta: v })}
          disabled={disabled}
          label="\Delta"
        />
      </div>

      <div style={{ display: "grid", gap: 6, maxWidth: 160 }}>
        <span>Liczba miejsc zerowych:</span>
        <SelectInput
          value={zerosStr}
          onChange={(e) =>
            onChange({
              ...curr,
              // zapisujemy STRING ("0" | "1" | "2") — checkAnswer to znormalizuje
              zeros: e?.target?.value ?? "",
            })
          }
          options={[
            { value: "", label: "— wybierz —" },
            { value: "0", label: "0" },
            { value: "1", label: "1" },
            { value: "2", label: "2" },
          ]}
          // jeśli SelectInput obsługuje dodatkowe propsy rozmiaru/klasy, można dodać:
          // className="select-compact"  // lub style wewnętrzne
        />
      </div>
    </div>
  );
}

/* ---------- CHECK ---------- */

export function checkAnswer({ data }, userAnswer) {
  try {
    if (!userAnswer) return false;

    const rawDelta = userAnswer.delta?.raw ?? userAnswer.delta ?? "";
    const parsedDelta = parseFractionLike(rawDelta);
    if (parsedDelta == null || !Number.isFinite(parsedDelta)) return false;

    const zerosGiven = normalizeZeros(userAnswer.zeros); // 0/1/2 lub null (ze stringa/obiektu/numbera)
    if (zerosGiven == null) return false;

    const correctZeros = data.delta > 0 ? 2 : (data.delta === 0 ? 1 : 0);

    return parsedDelta === data.delta && zerosGiven === correctZeros;
  } catch {
    return false;
  }
}

/* ---------- ANSWER ---------- */

export function renderAnswer({ data }) {
  const { delta } = data;
  const zeros = delta > 0 ? 2 : (delta === 0 ? 1 : 0);
  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`\\Delta = ${delta}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`\\text{liczba miejsc zerowych} = ${zeros}`} />
    </div>
  );
}

/* ---------- EXPLANATION ---------- */

export function renderExplanation({ data }) {
  const { a, b, c, delta } = data;
  const body = formatQuadraticLatex(a, b, c);
  const zeros = delta > 0 ? 2 : (delta === 0 ? 1 : 0);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7, marginTop: 8 }}>
      <div><strong>Krok 1.</strong> Wzór na deltę:</div>
      <BlockMath math={'\\Delta = b^2 - 4ac'} />
      <div><strong>Krok 2.</strong> Podstawiamy z funkcji:</div>
      <BlockMath math={`f(x) = ${body}`} />
      <BlockMath math={`\\Delta = ${b}^2 - 4\\cdot ${a}\\cdot ${c}`} />
      <div><strong>Krok 3.</strong> Obliczamy i wnioskujemy o liczbie miejsc zerowych:</div>
      <BlockMath math={`\\Delta = ${delta}`} />
      <div>
        Jeśli <InlineMath math={'\\Delta > 0'} /> – dwa miejsca zerowe, jeśli{" "}
        <InlineMath math={'\\Delta = 0'} /> – jedno, a jeśli{" "}
        <InlineMath math={'\\Delta < 0'} /> – brak.
      </div>
      <div>
        Zatem liczba miejsc zerowych: <InlineMath math={`${zeros}`} />.
      </div>
    </div>
  );
}

/* ---------- USER ANSWER (odporna na kolejność argumentów) ---------- */

export function renderUserAnswer(arg1, arg2) {
  const looksLikeTask = (x) => x && typeof x === "object" && x.data && typeof x.data === "object";
  const task = looksLikeTask(arg1) ? arg1 : (looksLikeTask(arg2) ? arg2 : { data: {} });
  const userAnswer = looksLikeTask(arg1) ? arg2 : arg1;

  const deltaRaw = userAnswer?.delta?.raw ?? userAnswer?.delta ?? "";
  const zerosVal = normalizeZeros(userAnswer?.zeros); // 0/1/2 lub null

  const emptyDelta = !String(deltaRaw ?? "").trim();
  const emptyZeros = (zerosVal == null);

  if (emptyDelta && emptyZeros) return <i>(brak odpowiedzi)</i>;

  const show = (r) => {
    const s = String(r ?? "").trim();
    return s ? prettyRawToLatex(r) : "?";
  };

  return (
    <div style={{ marginTop: 6, marginBottom: 6 }}>
      <InlineMath math={`\\Delta = ${show(deltaRaw)}`} />
      <span style={{ margin: "0 10px" }} />
      <InlineMath math={`\\text{liczba miejsc zerowych} = ${emptyZeros ? "?" : zerosVal}`} />
    </div>
  );
}

/* ---------- EXPORT DEFAULT ---------- */

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
